import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import { Periods } from '@app/src/components/CreateNewRequestModal/PeriodSelector/PeriodSelector'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import type { Spend } from '@app/src/types/organizations'
import { NotificationSeverity } from '@app/src/wf-constants'
import { Stack } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import Select from '../../Form/Select/ControlledSelect'
import TextField from '../../Ui/TextField'
import { useDrawer } from '../DrawerContext'
import type { DrawerViewProps } from '../DrawerView'
import DrawerView from '../DrawerView'

type DrawerViewAddAnnualSpendProps = {
  providerId: number
  periodsWithoutSpend: {
    value: number
    label: string
  }[]
  spend?: Spend
  preselectedPeriodName?: string
} & Omit<DrawerViewProps, 'title'>

const DrawerViewAddAnnualSpend: React.FC<DrawerViewAddAnnualSpendProps> = ({
  providerId,
  spend,
  periodsWithoutSpend,
  preselectedPeriodName,
  ...props
}) => {
  const { formatMessage } = useIntl()
  const { showSnackbar } = useSnackbar()
  const { closeDrawer } = useDrawer()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()
  const { setValue, control, register, errors, handleSubmit } = useForm()
  const { mutateAsync: saveSpendAsync, isPending: isSavingSpend } = useUpdateResource<Spend, Partial<Spend>>()
  const preselectedPeriodValue = Number(preselectedPeriodName)
  const hasValidPreselectedPeriod = Number.isFinite(preselectedPeriodValue)

  const periodsToDisplay = [
    ...(spend ? [{ value: Number(spend.periodName), label: spend.periodName }] : []),
    ...(preselectedPeriodName && hasValidPreselectedPeriod
      ? [{ value: preselectedPeriodValue, label: preselectedPeriodName }]
      : []),
    ...periodsWithoutSpend,
  ]
    .filter((period, index, array) => array.findIndex(p => p.value === period.value) === index)
    .sort((a, b) => b.value - a.value)

  const isEditMode = Boolean(spend)

  const onSubmit = async (values: Spend) => {
    const body = {
      ...values,
      providerId,
      id: spend?.id,
      periodType: Periods.Yearly,
    }
    await saveSpendAsync(
      {
        url: endpoints.providerAnnualSpendSave,
        body,
      },
      {
        onSuccess: () => {
          showSnackbar({
            message: formatMessage({
              id: isEditMode
                ? 'schemas.supplier.annualSpend.spendEditedSuccessfully'
                : 'schemas.supplier.annualSpend.spendAddedSuccessfully',
            }),
            severity: NotificationSeverity.success,
          })
          queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderCollection] })
          queryClient.invalidateQueries({ queryKey: [FetchKey.ProviderFacets] })
          closeDrawer()
        },
        onError: error => {
          showErrorNotification({ requestError: error })
        },
      },
    )
  }

  return (
    <DrawerView
      title={formatMessage({ id: 'schemas.supplier.annualSpend.spend' })}
      subTitle={spend?.periodName}
      buttons={[
        {
          label: formatMessage({ id: 'general.close' }),
          onClick: closeDrawer,
          disabled: isSavingSpend,
        },
        {
          label: formatMessage({ id: 'general.submit' }),
          variant: 'contained',
          type: 'submit',
          loading: isSavingSpend,
        },
      ]}
      stackButtons
      {...props}
      onFormSubmit={handleSubmit(onSubmit)}
    >
      <Stack p={2} spacing={3}>
        <Select
          options={periodsToDisplay}
          fieldLabel={formatMessage({ id: 'schemas.supplier.annualSpend.periodName' })}
          onInputChange={(e, value) => {
            if (!value) setValue('periodName', undefined)
          }}
          defaultValue={
            Number(spend?.periodName) ||
            (hasValidPreselectedPeriod ? preselectedPeriodValue : undefined) ||
            periodsToDisplay[0].value
          }
          enableAutoSelect
          name="periodName"
          control={control}
        />
        <TextField
          hoveringLabel
          inputRef={register({ required: formatMessage({ id: 'form.validation.required' }) })}
          fullWidth
          defaultValue={spend?.amount}
          name="amount"
          label={formatMessage({ id: 'schemas.supplier.annualSpend.amount' })}
          error={Boolean(errors?.amount)}
          type="number"
          helperText={errors?.amount?.message}
          onClear={(): void => setValue('amount', '')}
        />
      </Stack>
    </DrawerView>
  )
}

export default DrawerViewAddAnnualSpend
